import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { OwnerLayout } from '../../components/layouts/OwnerLayout';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  Plus, Search, MapPin, Phone, Mail, Edit, Trash2, User, MoreHorizontal, 
  FileText, Calendar, CheckCircle2, Clock, AlertTriangle, ChevronRight, 
  Download, Eye, X, Building2, Upload, FileSpreadsheet
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AddressAutocomplete } from '../../components/ui/address-autocomplete';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const initialFormState = {
  name: '', email: '', phone: '', address: '', city: '', postal_code: '', 
  pool_type: '', // cloro, sal, etc.
  is_rectangular: true,
  pool_length: '', // largo
  pool_width: '', // ancho
  pool_side3: '', // lado 3 (para piscinas no rectangulares)
  pool_side4: '', // lado 4 (para piscinas no rectangulares)
  notes: '',
  send_report_email: false // enviar PDF por email al completar trabajo
};

const Clients = () => {
  const { api } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientJobs, setClientJobs] = useState([]);
  const [clientReports, setClientReports] = useState([]);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await api.get('/clients');
      setClients(res.data);
    } catch (error) {
      toast.error('Error al cargar los clientes');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientDetails = async (client) => {
    setSelectedClient(client);
    setDetailDialogOpen(true);
    setLoadingDetails(true);
    
    try {
      const [jobsRes, reportsRes] = await Promise.all([
        api.get('/jobs', { params: { client_id: client.id } }),
        api.get('/work-reports', { params: { client_id: client.id } })
      ]);
      setClientJobs(jobsRes.data);
      setClientReports(reportsRes.data);
    } catch (error) {
      toast.error('Error al cargar detalles');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Solo el nombre es obligatorio
    if (!formData.name || formData.name.trim() === '') {
      toast.error('El nombre del cliente es obligatorio');
      return;
    }
    
    setSubmitting(true);
    try {
      // Build pool_dimensions string
      let pool_dimensions = '';
      if (formData.pool_length && formData.pool_width) {
        if (formData.is_rectangular) {
          pool_dimensions = `${formData.pool_length}x${formData.pool_width}`;
        } else {
          pool_dimensions = `${formData.pool_length}x${formData.pool_width}x${formData.pool_side3 || formData.pool_width}x${formData.pool_side4 || formData.pool_length}`;
        }
      }
      
      const submitData = {
        name: formData.name.trim(),
        email: formData.email || '',
        phone: formData.phone || '',
        address: formData.address || '',
        city: formData.city || '',
        postal_code: formData.postal_code || '',
        pool_type: formData.pool_type || '',
        pool_dimensions: pool_dimensions,
        notes: formData.notes || '',
        send_report_email: formData.send_report_email || false
      };
      
      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, submitData);
        toast.success('Cliente actualizado');
      } else {
        await api.post('/clients', submitData);
        toast.success('Cliente creado');
      }
      setDialogOpen(false);
      setFormData(initialFormState);
      setEditingClient(null);
      fetchClients();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al guardar el cliente'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    // Parse pool_dimensions if it exists (format: "10x5" or "10x5x8x6")
    let poolDimensions = { length: '', width: '', side3: '', side4: '', isRectangular: true };
    if (client.pool_dimensions) {
      const dims = client.pool_dimensions.split('x');
      if (dims.length >= 2) {
        poolDimensions.length = dims[0];
        poolDimensions.width = dims[1];
        if (dims.length === 4) {
          poolDimensions.side3 = dims[2];
          poolDimensions.side4 = dims[3];
          poolDimensions.isRectangular = false;
        }
      }
    }
    
    setFormData({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      city: client.city || '',
      postal_code: client.postal_code || '',
      pool_type: client.pool_type || '',
      is_rectangular: poolDimensions.isRectangular,
      pool_length: poolDimensions.length,
      pool_width: poolDimensions.width,
      pool_side3: poolDimensions.side3,
      pool_side4: poolDimensions.side4,
      notes: client.notes || '',
      send_report_email: client.send_report_email || false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (clientId) => {
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success('Cliente eliminado');
      fetchClients();
    } catch (error) {
      toast.error('Error al eliminar el cliente');
    }
  };

  const downloadPdf = async (reportId) => {
    try {
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/work-reports/${reportId}/pdf`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `parte_${reportId.slice(0, 8)}.pdf`;
      link.click();
    } catch (error) {
      toast.error('Error al descargar PDF');
    }
  };

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.address && c.address.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (c.city && c.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (c.phone && c.phone.includes(searchTerm))
  );

  const getClientStats = (clientId) => {
    // This could be enhanced with actual stats from API
    const jobs = clientJobs.filter(j => j.client_id === clientId);
    const completed = jobs.filter(j => j.status === 'completed').length;
    return { total: jobs.length, completed };
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      scheduled: 'Programado',
      in_progress: 'En progreso',
      completed: 'Completado',
      cancelled: 'Cancelado'
    };
    return <Badge className={styles[status]}>{labels[status] || status}</Badge>;
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(process.env.REACT_APP_BACKEND_URL + '/api/export/clients?format=csv', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'clientes_export.csv';
      link.click();
      toast.success('Clientes exportados a CSV');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(process.env.REACT_APP_BACKEND_URL + '/api/export/clients/pdf', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'clientes_export.pdf';
      link.click();
      toast.success('Clientes exportados a PDF');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(process.env.REACT_APP_BACKEND_URL + '/api/import/clients', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formDataUpload
      });
      const result = await response.json();
      
      if (result.imported > 0) {
        toast.success(`${result.imported} clientes importados correctamente`);
        fetchClients();
      }
      if (result.errors > 0) {
        toast.warning(`${result.errors} errores durante la importación`);
      }
    } catch (error) {
      toast.error('Error al importar');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout>
      <div className="space-y-6" data-testid="clients-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">CRM - Clientes</h1>
            <p className="text-slate-500">{clients.length} clientes registrados</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Export/Import Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Exportar / Importar
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportCSV}>
                  <Download className="w-4 h-4 mr-2" />
                  Exportar a Excel (CSV)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportPDF}>
                  <FileText className="w-4 h-4 mr-2" />
                  Exportar a PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                  <Upload className="w-4 h-4 mr-2" />
                  Importar desde CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv"
              onChange={handleImport}
              className="hidden"
            />
            <Dialog open={dialogOpen} onOpenChange={(open) => { 
              setDialogOpen(open); 
              if (!open) { 
                setEditingClient(null); 
                setFormData(initialFormState); 
              } 
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-client-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo cliente
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingClient ? 'Editar cliente' : 'Nuevo cliente'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre *</Label>
                    <Input 
                      value={formData.name} 
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                      required 
                      data-testid="client-name-input" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input 
                      type="email" 
                      value={formData.email} 
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                      data-testid="client-email-input" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <Input 
                      value={formData.phone} 
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })} 
                      data-testid="client-phone-input" 
                    />
                  </div>
                  <div className="space-y-2 col-span-1 sm:col-span-2">
                    <Label>Dirección</Label>
                    <AddressAutocomplete
                      value={formData.address}
                      onChange={(value) => setFormData({ ...formData, address: value })}
                      onAddressSelect={(addressData) => {
                        setFormData(prev => ({
                          ...prev,
                          address: addressData.address,
                          city: addressData.city || prev.city,
                          postal_code: addressData.postal_code || prev.postal_code
                        }));
                      }}
                      placeholder="Escribe la dirección..."
                      data-testid="client-address-input"
                    />
                    <p className="text-xs text-slate-500">Escribe al menos 4 letras para ver sugerencias, o escribe la dirección manualmente</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Ciudad</Label>
                    <Input 
                      value={formData.city} 
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })} 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Código postal</Label>
                    <Input 
                      value={formData.postal_code} 
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} 
                    />
                  </div>
                </div>
                
                {/* Pool Type */}
                <div className="space-y-2">
                  <Label>Tipo de tratamiento de agua</Label>
                  <Select value={formData.pool_type} onValueChange={(v) => setFormData({ ...formData, pool_type: v })}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar tipo" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cloro">Cloro</SelectItem>
                      <SelectItem value="sal">Cloración salina (Sal)</SelectItem>
                      <SelectItem value="bromo">Bromo</SelectItem>
                      <SelectItem value="ozono">Ozono</SelectItem>
                      <SelectItem value="uv">Ultravioleta (UV)</SelectItem>
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Pool Dimensions */}
                <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">Medidas de la piscina (metros)</Label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_rectangular}
                        onChange={(e) => setFormData({ ...formData, is_rectangular: e.target.checked })}
                        className="rounded border-slate-300"
                      />
                      Rectangular
                    </label>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Lado 1 (largo)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="m"
                        value={formData.pool_length}
                        onChange={(e) => setFormData({ ...formData, pool_length: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Lado 2 (ancho)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="m"
                        value={formData.pool_width}
                        onChange={(e) => setFormData({ ...formData, pool_width: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">
                        Lado 3 {formData.is_rectangular && <span className="text-slate-400">(= Lado 1)</span>}
                      </Label>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="m"
                        value={formData.is_rectangular ? formData.pool_length : formData.pool_side3}
                        onChange={(e) => setFormData({ ...formData, pool_side3: e.target.value })}
                        disabled={formData.is_rectangular}
                        className={formData.is_rectangular ? 'bg-slate-100' : ''}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">
                        Lado 4 {formData.is_rectangular && <span className="text-slate-400">(= Lado 2)</span>}
                      </Label>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="m"
                        value={formData.is_rectangular ? formData.pool_width : formData.pool_side4}
                        onChange={(e) => setFormData({ ...formData, pool_side4: e.target.value })}
                        disabled={formData.is_rectangular}
                        className={formData.is_rectangular ? 'bg-slate-100' : ''}
                      />
                    </div>
                  </div>
                  {formData.pool_length && formData.pool_width && (
                    <p className="text-sm text-slate-500">
                      Área aproximada: {(parseFloat(formData.pool_length) * parseFloat(formData.pool_width)).toFixed(1)} m²
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label>Notas</Label>
                  <Textarea 
                    value={formData.notes} 
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })} 
                    rows={3} 
                    placeholder="Notas especiales sobre el cliente o su piscina..."
                  />
                </div>
                
                {/* Email notification option */}
                {formData.email && (
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <input
                      type="checkbox"
                      id="send_report_email"
                      checked={formData.send_report_email}
                      onChange={(e) => setFormData({ ...formData, send_report_email: e.target.checked })}
                      className="rounded border-slate-300 text-sky-600"
                    />
                    <label htmlFor="send_report_email" className="text-sm cursor-pointer">
                      <span className="font-medium text-slate-700">Enviar PDF por email</span>
                      <p className="text-slate-500 text-xs">El cliente recibirá automáticamente el parte de trabajo por email al completarse</p>
                    </label>
                  </div>
                )}
                
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="w-full sm:w-auto">Cancelar</Button>
                  <Button type="submit" disabled={submitting} data-testid="save-client-btn" className="w-full sm:w-auto">
                    {submitting ? 'Guardando...' : editingClient ? 'Guardar cambios' : 'Crear cliente'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input 
            placeholder="Buscar por nombre, dirección, teléfono..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            className="pl-10" 
            data-testid="search-clients-input" 
          />
        </div>

        {/* Client Cards */}
        {filteredClients.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <User className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">{searchTerm ? 'No se encontraron clientes' : 'Aún no tienes clientes'}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClients.map((client, index) => (
              <motion.div 
                key={client.id} 
                initial={{ opacity: 0, y: 10 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: index * 0.03 }}
              >
                <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div 
                        className="flex-1 cursor-pointer" 
                        onClick={() => fetchClientDetails(client)}
                      >
                        <CardTitle className="text-lg font-semibold group-hover:text-sky-600 transition-colors">
                          {client.name}
                        </CardTitle>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => fetchClientDetails(client)}>
                            <Eye className="w-4 h-4 mr-2" />Ver detalles
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(client)}>
                            <Edit className="w-4 h-4 mr-2" />Editar
                          </DropdownMenuItem>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" />Eliminar
                              </DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>¿Eliminar cliente?</AlertDialogTitle>
                                <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete(client.id)} className="bg-red-600 hover:bg-red-700">
                                  Eliminar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent 
                    className="space-y-2 text-sm cursor-pointer" 
                    onClick={() => fetchClientDetails(client)}
                  >
                    <div className="flex items-start gap-2 text-slate-600">
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{client.address}{client.city && `, ${client.city}`}</span>
                    </div>
                    {client.phone && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="w-4 h-4" />
                        <span>{client.phone}</span>
                      </div>
                    )}
                    {client.email && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="w-4 h-4" />
                        <span className="truncate">{client.email}</span>
                      </div>
                    )}
                    {(client.pool_type || client.pool_dimensions) && (
                      <div className="flex flex-wrap gap-2 pt-2 border-t">
                        {client.pool_type && (
                          <Badge variant="outline" className="text-xs capitalize">{client.pool_type}</Badge>
                        )}
                        {client.pool_dimensions && (
                          <Badge variant="outline" className="text-xs">{client.pool_dimensions} m</Badge>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Client Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                {selectedClient?.name}
              </DialogTitle>
            </DialogHeader>
            
            {loadingDetails ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
              </div>
            ) : (
              <Tabs defaultValue="info" className="space-y-4">
                <TabsList>
                  <TabsTrigger value="info">Información</TabsTrigger>
                  <TabsTrigger value="jobs">Trabajos ({clientJobs.length})</TabsTrigger>
                  <TabsTrigger value="reports">Partes ({clientReports.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="info" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm text-slate-500">Dirección</p>
                      <p className="font-medium">{selectedClient?.address}</p>
                      {selectedClient?.city && <p className="text-sm text-slate-600">{selectedClient?.city} {selectedClient?.postal_code}</p>}
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-slate-500">Contacto</p>
                      {selectedClient?.phone && <p className="font-medium">{selectedClient?.phone}</p>}
                      {selectedClient?.email && <p className="text-sm text-slate-600">{selectedClient?.email}</p>}
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-slate-500">Piscina</p>
                      <p className="font-medium capitalize">{selectedClient?.pool_type || '-'}</p>
                      <p className="text-sm text-slate-600">{selectedClient?.pool_dimensions ? `${selectedClient?.pool_dimensions} m` : '-'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-slate-500">Estadísticas</p>
                      <p className="font-medium">{clientJobs.length} trabajos totales</p>
                      <p className="text-sm text-slate-600">{clientReports.length} partes completados</p>
                    </div>
                  </div>
                  {selectedClient?.notes && (
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500 mb-1">Notas</p>
                      <p>{selectedClient?.notes}</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="jobs" className="space-y-3">
                  {clientJobs.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No hay trabajos para este cliente</p>
                  ) : (
                    clientJobs.map(job => (
                      <div key={job.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium">{job.title}</h4>
                            {job.is_emergency && <Badge className="bg-red-500 text-white text-xs">URGENTE</Badge>}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {job.scheduled_date}
                            </span>
                            {job.worker_name && (
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {job.worker_name}
                              </span>
                            )}
                          </div>
                        </div>
                        {getStatusBadge(job.status)}
                      </div>
                    ))
                  )}
                </TabsContent>

                <TabsContent value="reports" className="space-y-3">
                  {clientReports.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No hay partes de trabajo para este cliente</p>
                  ) : (
                    clientReports.map(report => (
                      <div key={report.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm text-slate-500">
                              {format(new Date(report.completed_at), "d MMM yyyy", { locale: es })}
                            </span>
                            <Badge variant="outline">
                              {(report.checklist || []).filter(c => c.checked).length}/{(report.checklist || []).length} tareas
                            </Badge>
                            {report.photos?.length > 0 && (
                              <Badge variant="outline">{report.photos.length} fotos</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-600">Técnico: {report.worker_name}</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => downloadPdf(report.id)}>
                          <Download className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                      </div>
                    ))
                  )}
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </OwnerLayout>
  );
};

export default Clients;
